import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { Outlet, useLocation, useParams } from "react-router-dom";
import { DEFAULT_KNOWLEDGE_INSTANCE_ID } from "../routes.js";
import {
  KnowledgeSearchDialog,
  type KnowledgeSearchPreset,
} from "./KnowledgeSearch.js";

type OpenKnowledgeSearch = (preset?: KnowledgeSearchPreset) => void;

const KnowledgeSearchContext = createContext<OpenKnowledgeSearch | null>(null);

export function useOpenKnowledgeSearch(): OpenKnowledgeSearch {
  const openSearch = useContext(KnowledgeSearchContext);
  if (!openSearch) {
    throw new Error(
      "useOpenKnowledgeSearch must be used inside KnowledgeStudioFrame",
    );
  }
  return openSearch;
}

export function KnowledgeStudioFrame(): JSX.Element {
  const { instanceId = DEFAULT_KNOWLEDGE_INSTANCE_ID } = useParams<{
    instanceId: string;
  }>();
  const { pathname } = useLocation();
  const frameRef = useRef<HTMLDivElement>(null);
  const [searchRequest, setSearchRequest] = useState<{
    readonly open: boolean;
    readonly preset: KnowledgeSearchPreset | undefined;
    readonly requestId: number;
  }>({ open: false, preset: undefined, requestId: 0 });
  const openSearch = useCallback((preset?: KnowledgeSearchPreset) => {
    setSearchRequest((current) => ({
      open: true,
      preset,
      requestId: current.requestId + 1,
    }));
  }, []);

  useLayoutEffect(() => {
    const scrollRegion = frameRef.current?.closest("main");
    if (scrollRegion) {
      scrollRegion.scrollTop = 0;
      scrollRegion.scrollLeft = 0;
      return;
    }
    window.scrollTo({ top: 0, left: 0 });
  }, [pathname]);

  useEffect(() => {
    const onKeyDown = (event: globalThis.KeyboardEvent) => {
      const target = event.target;
      const isEditing =
        target instanceof HTMLInputElement ||
        target instanceof HTMLTextAreaElement ||
        (target instanceof HTMLElement && target.isContentEditable);
      if (
        event.key === "/" &&
        !searchRequest.open &&
        !isEditing &&
        !event.metaKey &&
        !event.ctrlKey
      ) {
        event.preventDefault();
        openSearch();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [openSearch, searchRequest.open]);

  return (
    <KnowledgeSearchContext.Provider value={openSearch}>
      {/* The frame is the size container every Knowledge Studio page queries
          (PB-5436): padding-free and as wide as main, so its md/lg/xl rules
          switch at the window breakpoints minus the 1rem scrollbar allowance
          while the rail is closed, and follow main when the Player AI side
          rail narrows it. */}
      <div
        key={pathname}
        ref={frameRef}
        className="@container/knowledge-studio min-h-full bg-pb-page text-pb-text"
      >
        <Outlet />
      </div>
      <KnowledgeSearchDialog
        key={searchRequest.requestId}
        instanceId={instanceId}
        open={searchRequest.open}
        preset={searchRequest.preset}
        onClose={() =>
          setSearchRequest((current) => ({ ...current, open: false }))
        }
      />
    </KnowledgeSearchContext.Provider>
  );
}
